import type { Point } from "./types";

/** Straight-line distance between two planar points. */
export function distance(a: Point, b: Point): number {
  const dx = a[0] - b[0], dy = a[1] - b[1];
  return Math.sqrt(dx * dx + dy * dy);
}

/** Full Euclidean distance matrix for a list of points (dist[i][j]). */
export function euclideanMatrix(points: Point[]): number[][] {
  const n = points.length;
  const d: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      const v = distance(points[i], points[j]);
      d[i][j] = v;
      d[j][i] = v;
    }
  return d;
}

/**
 * Total length of a closed tour. `order` is a node sequence; the leg from the
 * last node back to the first is always included, so callers may pass either an
 * open sequence [0,2,1] or an explicitly closed one [0,2,1,0] — the closing leg
 * is counted once either way (a trailing repeat of the first node adds a 0-leg).
 */
export function tourLength(order: number[], dist: number[][]): number {
  if (order.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < order.length; i++) {
    const a = order[i];
    const b = order[(i + 1) % order.length];
    total += dist[a][b];
  }
  return total;
}

/**
 * A nearest-neighbour tour from the depot (node 0): repeatedly hop to the
 * closest unvisited node. Returns an open order [0, …] (no trailing depot).
 * This is a fast, honest *heuristic* baseline — not guaranteed optimal — handy
 * as a player hint or a sanity check against the exact solver.
 */
export function nearestNeighborTour(dist: number[][], start = 0): number[] {
  const n = dist.length;
  if (n === 0) return [];
  const visited = new Array<boolean>(n).fill(false);
  const order = [start];
  visited[start] = true;
  let cur = start;
  for (let step = 1; step < n; step++) {
    let best = -1, bestD = Infinity;
    for (let j = 0; j < n; j++) {
      if (!visited[j] && dist[cur][j] < bestD) {
        bestD = dist[cur][j];
        best = j;
      }
    }
    if (best === -1) break;
    visited[best] = true;
    order.push(best);
    cur = best;
  }
  return order;
}
